<template>
  <div v-if="socials" class="social-kit">
    <NuxtLink
      v-for="social in socials"
      :key="social.title"
      :aria-label="`EenVandaag op ${social.title}`"
      class="social-kit-button"
      :to="social.url"
      target="_blank"
      @click="handleLinkClick(social.title, social.url)"
      @click.middle="handleLinkClick(social.title, social.url)"
    >
      <Icon
        :name="social.icon"
        class="social-kit-button-icon"
        :class="`i-${social.icon.replace(/:/g, '-')}`"
        aria-hidden="true"
        role="presentation"
      />
    </NuxtLink>
  </div>
</template>

<script setup lang="ts">
import siteConfig from '~~/configs/site.config';
import type { IAnalytics } from '~~/types/local_types';

const socials = siteConfig?.socials || [];

const props = defineProps({
  analytics: {
    type: Object as () => IAnalytics,
    default: null,
  },
});

const handleLinkClick = (linkText: string, linkUrl: string) => {
  gtmTrackEvent({
    linkText,
    linkUrl,
    ...props.analytics,
  });
};
</script>

<style scoped lang="postcss">
.social-kit {
  --social-kit-gap: calc(var(--base-spacing) * 5);

  color: hsl(var(--color-neutral-white));
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--social-kit-gap);
  flex-wrap: wrap;
}

.social-kit-button {
  --social-kit-button-size: calc(var(--base-spacing) * 11);
  --social-kit-border-radius: 50%;
  --social-kit-button-background: var(--effect-gradient-btn-primary);
  --social-kit-button-box-shadow: var(--effect-box-shadow-primary);

  background: var(--social-kit-button-background);
  box-shadow: var(--social-kit-button-box-shadow);
  height: var(--social-kit-button-size);
  width: var(--social-kit-button-size);
  border-radius: var(--social-kit-border-radius);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  position: relative;

  &:after,
  &:before {
    z-index: 1;
    opacity: 0;
    content: '';
    position: absolute;
    height: 100%;
    width: 100%;
    border-radius: var(--social-kit-border-radius);
    box-shadow: var(--social-kit-button-box-shadow);
    background: var(--social-kit-button-background);
    transition: opacity 250ms ease-in-out;
  }

  &:before {
    --social-kit-button-background: var(--effect-gradient-btn-primary-hover);
  }
  &:after {
    --social-kit-button-background: var(--effect-gradient-btn-primary-active);
  }

  @media (--md-viewport) {
    --social-kit-button-size: calc(var(--base-spacing) * 14);
  }

  @add-mixin hover {
    box-shadow: initial;

    &:before {
      opacity: 1;
    }
  }

  &:active {
    &:after {
      opacity: 1;
    }
  }

  &:focus-visible {
    --social-kit-button-box-shadow: var(--effect-box-shadow-focus-light);
  }
}

.social-kit-button-icon {
  --social-kit-button-icon-size: calc(var(--base-spacing) * 5);
  position: relative;
  z-index: 2;
  color: hsl(var(--color-neutral-white));

  font-size: var(--social-kit-button-icon-size);

  @media (--md-viewport) {
    --social-kit-button-icon-size: calc(var(--base-spacing) * 6);
  }

  &.i-eenvandaag-general-redit {
    font-size: calc(var(--social-kit-button-icon-size) + 3px);
  }
}
</style>
